import { AlertCircle } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { FinancialStatCard } from '@/components/custom/companies/overview/financial-stat-card'
import type { ExpandedSeriesFetcher } from '@/components/custom/companies/overview/financial-stat-card'
import {
  CARD_ORDER,
  DEFAULT_FINANCIAL_METRIC_CARD_LABELS,
  EMPTY_FINANCIAL_METRIC_CARD_SELECTION,
  type FinancialMetricCardKey,
  type FinancialMetricCardSelection,
  type ExpandedPeriodType,
} from '@/hooks/use-metrics'
import {
  usePortalMetricCards,
  usePortalMetricLabelOptions,
  usePortalExpandedMetricSeries,
} from '@/hooks/use-portal-metrics'
import { Typography } from '@/components/ui/typography'
import type { PortalTabValue } from '@/components/custom/portal/portal-tabs'

const PERIOD_TO_VIEW: Record<ExpandedPeriodType, string> = {
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  LTM: 'QUARTERLY',
  YTD: 'ANNUAL',
}

export function PortalFinancialStatCards({
  onNavigateToFinancials,
}: {
  onNavigateToFinancials: (
    tab: PortalTabValue,
    trailingPeriod?: string,
    view?: string
  ) => void
}) {
  const [selection, setSelection] = useState<FinancialMetricCardSelection>(
    () => EMPTY_FINANCIAL_METRIC_CARD_SELECTION
  )

  const { data: metricOptions = [] } = usePortalMetricLabelOptions()
  const metricOptionById = useMemo(
    () => new Map(metricOptions.map((opt) => [opt.id, opt])),
    [metricOptions]
  )

  const defaultMetricIdByCard = useMemo(() => {
    const map: Partial<Record<FinancialMetricCardKey, string>> = {}
    for (const key of CARD_ORDER) {
      const defaultLabeledAs = DEFAULT_FINANCIAL_METRIC_CARD_LABELS[key]
      const defaultOpt = metricOptions.find(
        (opt) => opt.labeledAs === defaultLabeledAs
      )
      if (defaultOpt) map[key] = defaultOpt.id
    }
    return map
  }, [metricOptions])

  const handleSelectMetric = useCallback(
    (cardKey: FinancialMetricCardKey, metricLabelId: string) => {
      const shouldResetToDefault =
        metricLabelId === defaultMetricIdByCard[cardKey]
      const nextMetricLabelId = shouldResetToDefault ? null : metricLabelId

      setSelection((prev) => {
        if (prev[cardKey] === nextMetricLabelId) return prev
        return { ...prev, [cardKey]: nextMetricLabelId }
      })
    },
    [defaultMetricIdByCard]
  )

  const { cards, status, isError } = usePortalMetricCards(selection)

  const expandedSeriesFetcher: ExpandedSeriesFetcher = (
    metricLabelId,
    periodType,
    enabled
  ) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return usePortalExpandedMetricSeries(metricLabelId, periodType, enabled)
  }

  const handleNavigateToFinancials = (selectedPeriod: ExpandedPeriodType) => {
    onNavigateToFinancials(
      'financials',
      selectedPeriod,
      PERIOD_TO_VIEW[selectedPeriod]
    )
  }

  if (isError) {
    return (
      <div className="flex h-40 items-center justify-center rounded-sm border border-border bg-white">
        <div className="flex items-center gap-2 text-primary/70">
          <AlertCircle className="h-4 w-4" />
          <Typography variant="body-sm">
            Failed to load financial metrics
          </Typography>
        </div>
      </div>
    )
  }

  const allSelectedMetricLabelIds = CARD_ORDER.map(
    (cardKey) => selection[cardKey] ?? cards[cardKey]?.metricLabelId
  ).filter((id): id is string => Boolean(id))

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
      {CARD_ORDER.map((key) => {
        const metric = cards[key]
        const selectedMetricLabelId = selection[key] ?? metric?.metricLabelId
        const selectedOption = selectedMetricLabelId
          ? metricOptionById.get(selectedMetricLabelId)
          : undefined
        const defaultLabeledAs = DEFAULT_FINANCIAL_METRIC_CARD_LABELS[key]
        const defaultOption = metricOptions.find(
          (opt) => opt.labeledAs === defaultLabeledAs
        )

        const title =
          selectedOption?.label ??
          (metric?.metricLabelId
            ? metricOptionById.get(metric.metricLabelId)?.label
            : undefined) ??
          metricOptions.find((opt) => opt.labeledAs === metric?.labeledAs)
            ?.label ??
          defaultOption?.label ??
          metric?.labeledAs ??
          defaultLabeledAs

        const disabledMetricLabelIds = allSelectedMetricLabelIds.filter(
          (id) => id !== selectedMetricLabelId
        )

        return (
          <FinancialStatCard
            key={key}
            companyId=""
            title={title}
            selectedMetricLabelId={selectedMetricLabelId ?? undefined}
            disabledMetricLabelIds={disabledMetricLabelIds}
            metricOptions={metricOptions}
            onSelectMetric={(metricLabelId) =>
              handleSelectMetric(key, metricLabelId)
            }
            value={metric?.latestValue ?? null}
            changePercent={metric?.changePercent ?? null}
            changeDisplay={metric?.changeDisplay ?? 'UNAVAILABLE'}
            comparisonQuarter={metric?.comparisonQuarter ?? null}
            comparisonValue={metric?.comparisonValue ?? null}
            points={metric?.points ?? []}
            isLoading={status[key].isLoading}
            expandedSeriesFetcher={expandedSeriesFetcher}
            onNavigateToFinancials={handleNavigateToFinancials}
          />
        )
      })}
    </div>
  )
}
